import { create } from "zustand";
import {
    createEmptyCartSnapshot,
    CART_STORAGE_KEY,
    readCartSnapshot,
} from "../storage/cartStorage";
import type {
    AddCartItemInput,
    CartItem,
    CartPersistedState,
    CartStore,
} from "../types/cart.types";
import { applyCartOperations } from "./cartOperations";
import {
    commitCartOperations,
    replaceLegacyCartItems,
    validateAndRepairCartPersistence,
} from "./cartCoordinator";

const readInitialSnapshot = (): CartPersistedState => {
    if (typeof window === "undefined") return createEmptyCartSnapshot();

    try {
        return readCartSnapshot();
    } catch (error) {
        console.error("No se pudo hidratar el carrito persistido.", error);
        return createEmptyCartSnapshot();
    }
};

const initialSnapshot = readInitialSnapshot();

export const useCartStore = create<CartStore>((set, get) => ({
    items: initialSnapshot.items,
    persistedItems: initialSnapshot.items,
    revision: initialSnapshot.revision,
    isOpen: false,
    isOverrideActive: false,

    hydrateCartItems: async (items) => {
        const result = await replaceLegacyCartItems(Array.isArray(items) ? items : []);
        set({
            items: result.items,
            persistedItems: result.items,
            revision: result.revision,
            isOverrideActive: false,
        });
    },

    beginCartOverride: (items) => {
        set({
            items: Array.isArray(items) ? items : [],
            isOverrideActive: true,
        });
    },

    endCartOverride: () => {
        let latestSnapshot: CartPersistedState;
        try {
            latestSnapshot = readCartSnapshot();
        } catch (error) {
            console.error(
                "No se pudo restaurar el carrito persistido al finalizar el override.",
                error,
            );
            latestSnapshot = {
                items: get().persistedItems,
                revision: get().revision,
                updatedAt: new Date().toISOString(),
            };
        }

        set({
            items: latestSnapshot.items,
            persistedItems: latestSnapshot.items,
            revision: latestSnapshot.revision,
            isOverrideActive: false,
        });
    },

    applyOperations: async (operations) => {
        if (operations.length === 0) return get().items;

        if (get().isOverrideActive) {
            const nextItems = applyCartOperations(get().items, operations);
            set({ items: nextItems });
            return nextItems;
        }

        const result = await commitCartOperations(operations, get().persistedItems);
        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: result.items,
                      revision: result.revision,
                  }
                : {
                      items: result.items,
                      persistedItems: result.items,
                      revision: result.revision,
                  },
        );

        return get().items;
    },

    validateAndRepairCart: async () => {
        const snapshot = await validateAndRepairCartPersistence();
        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  }
                : {
                      items: snapshot.items,
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  },
        );

        return get().items;
    },

    applyExternalSnapshot: (snapshot) => {
        if (snapshot.revision <= get().revision) return;

        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  }
                : {
                      items: snapshot.items,
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  },
        );
    },

    addToCart: async (item: AddCartItemInput) => {
        await get().applyOperations([{ type: "add", item }]);
    },

    removeFromCart: async (idProduct, idProductVariant) => {
        await get().applyOperations([{ type: "remove", idProduct, idProductVariant }]);
    },

    updateQuantity: async (idProduct, idProductVariant, quantity) => {
        await get().applyOperations([
            { type: "updateQuantity", idProduct, idProductVariant, quantity },
        ]);
    },

    clearCart: async () => {
        await get().applyOperations([{ type: "clear" }]);
        set({ isOpen: false });
    },

    openCart: () => {
        if (get().isOpen) return;

        set({ isOpen: true });
        void get()
            .validateAndRepairCart()
            .catch((error) => console.error("No se pudo validar el carrito al abrirlo.", error));
    },
    closeCart: () => set({ isOpen: false }),
    toggleCart: () => {
        const willOpen = !get().isOpen;
        set({ isOpen: willOpen });
        if (!willOpen) return;

        void get()
            .validateAndRepairCart()
            .catch((error) => console.error("No se pudo validar el carrito al abrirlo.", error));
    },
}));

const handleStorageChange = (event: StorageEvent): void => {
    if (event.key !== CART_STORAGE_KEY) return;

    try {
        useCartStore.getState().applyExternalSnapshot(readCartSnapshot());
    } catch (error) {
        console.error("No se pudo sincronizar el carrito desde otra pestaña.", error);
    }
};

if (typeof window !== "undefined") {
    const cartWindow = window as typeof window & {
        __ecommerceCartStorageListener?: (event: StorageEvent) => void;
    };

    if (cartWindow.__ecommerceCartStorageListener) {
        window.removeEventListener("storage", cartWindow.__ecommerceCartStorageListener);
    }

    window.addEventListener("storage", handleStorageChange);
    cartWindow.__ecommerceCartStorageListener = handleStorageChange;
}
